import { getMATViewAs } from '../utils/impersonation';
import { fetchWithTimeout, refreshAuthToken, TOKEN_EXPIRED_HEADER } from './refreshAuthToken';

export const VIEW_AS_CONTEXT_ENDPOINT = '/api/Context/ConnectCtx';

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Gets this tab a new view as token once the one in session storage has expired.
 * A view as token can't be refreshed itself, so ConnectCtx is asked for a new one with the MAT user's own
 * (refreshable) cookie, which re-checks they can still view the school. Sent without the Authorization header, as
 * the expired view as token would otherwise take precedence over the cookie.
 * Concurrent callers share the same request.
 * @returns true if a new view as token was stored
 */
export const refreshViewAsToken = (): Promise<boolean> => {
	if (!refreshInFlight) {
		refreshInFlight = requestViewAsToken(true).finally(() => {
			refreshInFlight = null;
		});
	}

	return refreshInFlight;
};

const requestViewAsToken = (canRefreshCookie: boolean): Promise<boolean> => {
	const { groupAccessId, matImpersonateClientId } = getMATViewAs();

	if (!groupAccessId || !matImpersonateClientId) {
		return Promise.resolve(false);
	}

	return fetchWithTimeout(VIEW_AS_CONTEXT_ENDPOINT, {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			viewAsClientId: matImpersonateClientId,
		},
		body: JSON.stringify({
			groupAccessId: Number(groupAccessId),
			matImpersonateClientId: Number(matImpersonateClientId),
		}),
	})
		.then(async (response) => {
			// The MAT user's own token has expired too: refresh it once, then ask again
			if (
				response.status === 401 &&
				canRefreshCookie &&
				response.headers.get(TOKEN_EXPIRED_HEADER) !== null
			) {
				return (await refreshAuthToken()) && requestViewAsToken(false);
			}

			if (!response.ok) {
				return false;
			}

			// No token means the MAT user can no longer view this school
			const viewAsToken = (await response.json())?.ImpersonationToken;

			if (!viewAsToken) {
				return false;
			}

			sessionStorage.setItem('impersonationToken', viewAsToken);
			return true;
		})
		.catch(() => false);
};
